import * as React from "react";
import { useHistory } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import type { AnalyticsRange, CollectionsClientRow, CollectionsMonth } from "api/billing";
import {
  AXIS_TICK,
  CHART_COLORS,
  ChartFrame,
  ChartTooltip,
  CURSOR_FILL,
  GRID_STROKE,
} from "Components/charts";
import { isConnected } from "Components/Tenants";
import {
  DataTable,
  EmptyState,
  FormField,
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from "Components/ui";
import { ReceiptText } from "Components/ui/icons";
import { downloadCsv, type CsvColumn } from "utils/download";
import { formatDate, formatINR, humanize, pluralize, todayIST } from "utils/format";
import { useBillingAnalyticsQuery } from "../data";
import {
  customRangeError,
  formatRange,
  presetRange,
  RANGE_PRESETS,
  type RangePresetId,
} from "../reportRanges";
import {
  CLIENT_CSV_COLUMNS,
  csvRupees,
  focusDrawer,
  ReportClient,
  ReportError,
  ReportSummary,
  ReportTableHeading,
} from "./ReportParts";
import { PAYMENT_METHOD_LABELS } from "./StatusChips";
import { DateInput } from "./ui";

const monthTick = new Intl.DateTimeFormat("en-IN", { month: "short", timeZone: "UTC" });
const monthLong = new Intl.DateTimeFormat("en-IN", {
  month: "short",
  year: "numeric",
  timeZone: "UTC",
});
// Axis labels the Indian way (₹70k, ₹1.4 L, ₹2 Cr); Intl's compact en-IN uses "T" for thousand.
const axisRupees = (paise: number): string => {
  const rupees = paise / 100;
  const short = (value: number, unit: string) => `₹${Number(value.toFixed(1))}${unit}`;
  if (rupees >= 1e7) {
    return short(rupees / 1e7, " Cr");
  }
  if (rupees >= 1e5) {
    return short(rupees / 1e5, " L");
  }
  if (rupees >= 1e3) {
    return short(rupees / 1e3, "k");
  }
  return `₹${Math.round(rupees)}`;
};

const monthDate = (month: string) => new Date(`${month}-01T00:00:00Z`);
const formatMonth = (month: string) => monthLong.format(monthDate(month));
const methodsText = (methods: Record<string, number>): string =>
  Object.entries(methods)
    .sort((a, b) => b[1] - a[1])
    .map(([method]) => PAYMENT_METHOD_LABELS[method] || humanize(method))
    .join(", ");
const sharePercent = (amount: number, total: number): number =>
  total > 0 ? Math.round((amount / total) * 1000) / 10 : 0;

/** Bar chart of the last 12 months; the months inside the chosen period are in full colour. */
const MonthlyChart: React.FC<{ months: CollectionsMonth[]; range: AnalyticsRange }> = ({
  months,
  range,
}) => {
  const first = range.from.slice(0, 7);
  const last = range.to.slice(0, 7);
  const inRange = (month: string) => month >= first && month <= last;
  const summary = `Collections per month, last 12 months: ${months
    .map((entry) => `${formatMonth(entry.month)} ${formatINR(entry.amount)}`)
    .join(", ")}.`;
  return (
    <ChartFrame summary={summary} className="h-44">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={months} margin={{ top: 4, right: 4, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke={GRID_STROKE} strokeDasharray="3 3" />
          <XAxis
            dataKey="month"
            tickFormatter={(month: string) => monthTick.format(monthDate(month))}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            interval="preserveStartEnd"
            minTickGap={6}
          />
          <YAxis
            tickFormatter={axisRupees}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={56}
            allowDecimals={false}
          />
          <Tooltip
            cursor={CURSOR_FILL}
            content={<ChartTooltip formatLabel={formatMonth} formatValue={formatINR} />}
          />
          <Bar
            dataKey="amount"
            name="Collected"
            radius={[4, 4, 0, 0]}
            maxBarSize={28}
            isAnimationActive={false}
          >
            {months.map((entry) => (
              <Cell
                key={entry.month}
                fill={CHART_COLORS[1]}
                fillOpacity={inRange(entry.month) ? 1 : 0.3}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
};

const buildColumns = (total: number): ColumnDef<CollectionsClientRow>[] => [
  {
    id: "client",
    header: "Client",
    cell: ({ row }) => <ReportClient client={row.original} />,
  },
  {
    id: "count",
    header: "Payments",
    meta: { align: "right" },
    cell: ({ row }) => <span className="tabular-nums">{row.original.count}</span>,
  },
  {
    id: "amount",
    header: "Collected",
    meta: { align: "right", className: "whitespace-nowrap" },
    cell: ({ row }) => (
      <span className="font-semibold tabular-nums text-foreground">
        {formatINR(row.original.amount)}
      </span>
    ),
  },
  {
    id: "share",
    header: "Share",
    meta: { className: "w-36" },
    cell: ({ row }) => {
      const percent = sharePercent(row.original.amount, total);
      return (
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-muted">
            <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
          </div>
          <span className="w-11 text-right text-xs tabular-nums text-muted-foreground">
            {percent}%
          </span>
        </div>
      );
    },
  },
  {
    id: "last",
    header: "Last payment",
    meta: { className: "whitespace-nowrap" },
    cell: ({ row }) => (
      <div>
        <p className="text-foreground">
          {row.original.lastPaidOn ? formatDate(row.original.lastPaidOn) : "—"}
        </p>
        <p className="max-w-[10rem] truncate text-xs text-muted-foreground">
          {methodsText(row.original.methods)}
        </p>
      </div>
    ),
  },
];

const csvColumns = (total: number): CsvColumn<CollectionsClientRow>[] => [
  ...CLIENT_CSV_COLUMNS,
  { header: "Payments", value: (row) => row.count },
  { header: "Collected (INR)", value: (row) => csvRupees(row.amount) },
  { header: "Share (%)", value: (row) => sharePercent(row.amount, total) },
  { header: "Last payment", value: (row) => row.lastPaidOn },
  { header: "Methods", value: (row) => methodsText(row.methods) },
];

/** Preset pills, plus two date fields for a custom period. */
const RangePicker: React.FC<{
  preset: RangePresetId;
  onPresetChange: (preset: RangePresetId) => void;
  custom: AnalyticsRange;
  onCustomChange: (range: AnalyticsRange) => void;
}> = ({ preset, onPresetChange, custom, onCustomChange }) => {
  const error = preset === "custom" ? customRangeError(custom) : null;
  return (
    <div className="space-y-3">
      <Tabs value={preset} onValueChange={(value) => onPresetChange(value as RangePresetId)}>
        <TabsList variant="pill" aria-label="Period" className="max-w-full overflow-x-auto">
          {RANGE_PRESETS.map((option) => (
            <TabsTrigger key={option.id} value={option.id}>
              {option.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {preset === "custom" && (
        <div className="grid gap-3 sm:grid-cols-2">
          <FormField label="From" error={error && !custom.from ? error : undefined}>
            <DateInput
              value={custom.from}
              max={custom.to || undefined}
              hasError={Boolean(error)}
              onChange={(from) => onCustomChange({ ...custom, from })}
            />
          </FormField>
          <FormField label="To" error={error && custom.from ? error : undefined}>
            <DateInput
              value={custom.to}
              min={custom.from || undefined}
              hasError={Boolean(error)}
              onChange={(to) => onCustomChange({ ...custom, to })}
            />
          </FormField>
        </div>
      )}
    </div>
  );
};

/** The drawer's content; mounted only while it is open, so nothing loads before that. */
const CollectionsBody: React.FC = () => {
  const history = useHistory();
  const today = todayIST();
  const [preset, setPreset] = React.useState<RangePresetId>("this_month");
  const [range, setRange] = React.useState<AnalyticsRange>(() => presetRange("this_month", today));
  const [custom, setCustom] = React.useState<AnalyticsRange>(range);
  const query = useBillingAnalyticsQuery(range);
  const data = query.data;
  const collections = data?.collections;
  const shownRange = data?.range || range;
  const total = collections?.total || 0;
  const clients = collections?.clients || [];
  const columns = React.useMemo(() => buildColumns(total), [total]);

  const choosePreset = (next: RangePresetId) => {
    setPreset(next);
    if (next === "custom") {
      setCustom(range);
    } else {
      setRange(presetRange(next, today));
    }
  };
  // A custom period is only fetched once it is valid; until then the last one stays on screen.
  const changeCustom = (next: AnalyticsRange) => {
    setCustom(next);
    if (!customRangeError(next)) {
      setRange(next);
    }
  };
  const exportCsv = () =>
    downloadCsv(
      clients,
      csvColumns(total),
      `collections-${shownRange.from}-to-${shownRange.to}.csv`
    );

  let content: React.ReactNode;
  if (query.isError && !data) {
    content = <ReportError error={query.error} onRetry={() => query.refetch()} />;
  } else {
    const loading = query.isLoading;
    content = (
      <div className="space-y-6">
        <ReportSummary
          loading={loading}
          figures={[
            { label: "Collected", value: formatINR(total), hint: formatRange(shownRange) },
            { label: "Payments", value: collections?.count ?? 0, hint: "Reversals left out" },
            {
              label: "Paying clients",
              value: clients.length,
              hint: clients[0] ? `Most from ${clients[0].tenantName}` : undefined,
            },
          ]}
        />

        <section aria-label="Last 12 months" className="space-y-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">Last 12 months</h3>
            <p className="text-xs text-muted-foreground">Months in this period are highlighted</p>
          </div>
          {collections ? (
            <MonthlyChart months={collections.byMonth} range={shownRange} />
          ) : (
            <Skeleton className="h-44 w-full" />
          )}
        </section>

        <section aria-label="By client" className="space-y-3">
          <ReportTableHeading
            title="By client"
            detail="Largest first. Open a connected client to see its payments."
            onExport={clients.length > 0 ? exportCsv : undefined}
          />
          <DataTable
            columns={columns}
            data={clients}
            loading={loading}
            getRowId={(row) => row.tenantId}
            onRowClick={(row) => history.push(`/billing/${row.tenantId}?tab=payments`)}
            isRowClickable={(row) => isConnected(row.link)}
            enableSorting={false}
            density="compact"
            caption="Collections by client"
            className={query.isPreviousData ? "opacity-60 transition-opacity" : undefined}
            empty={
              <EmptyState
                compact
                icon={<ReceiptText />}
                title="No payments in this period"
                description="Choose another period above."
              />
            }
            footer={
              clients.length > 0 ? (
                <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span className="text-muted-foreground">
                    {pluralize(clients.length, "client")} ·{" "}
                    {pluralize(collections?.count || 0, "payment")}
                  </span>
                  <span className="font-semibold tabular-nums text-foreground">
                    Total {formatINR(total)}
                  </span>
                </div>
              ) : undefined
            }
          />
        </section>
      </div>
    );
  }

  return (
    <>
      <SheetHeader
        title="Collections"
        description="Who paid how much in a period. Only payments that went through count; reversed ones are left out."
      />
      <SheetBody className="space-y-6">
        <RangePicker
          preset={preset}
          onPresetChange={choosePreset}
          custom={custom}
          onCustomChange={changeCustom}
        />
        {content}
      </SheetBody>
    </>
  );
};

interface CollectionsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Collections per client for a period, with the 12-month trend (from the "Collected" tile). */
const CollectionsSheet: React.FC<CollectionsSheetProps> = ({ open, onOpenChange }) => (
  <Sheet open={open} onOpenChange={onOpenChange}>
    <SheetContent size="xl" onOpenAutoFocus={focusDrawer}>
      <CollectionsBody />
    </SheetContent>
  </Sheet>
);

export default CollectionsSheet;
